<template>
	<TheForm
		:model="request"
		:rules="rules"
		:id="id"
		ref="formRef"
		:waiting="waiting"
		:waitingMsg="texts.formErrors.waiting"
		:showErr="showErr"
		:errMsg="errorMsg"
		:submitFunc="submitForm"
	>
		<el-form-item label="Passwort vergeben" prop="password">
			<el-input type="password" v-model="request.password" placeholder="Passwort eingeben" show-password />
		</el-form-item>
		<el-form-item label="Passwort bestätigen" prop="passwordConfirm">
			<el-input type="password" v-model="request.passwordConfirm" placeholder="Passwort wiederholen" show-password />
		</el-form-item>
	</TheForm>

	<div class="button-panel">
		<TheButton v-if="!confirmationError && !showErr" @buttonclick="submitForm()" :loading="waiting" type="danger" isSubmit>
			Passwort bestätigen
		</TheButton>
		<TheButton v-if="confirmationError" @buttonclick="resendConfirmationEmail()" :loading="waiting" type="danger" isSubmit>
			Neuen Link senden
		</TheButton>
	</div>
</template>

<script lang="ts">
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import { texts, translateToGerman } from '../../utils/texts';
import { useStore } from '../../store';
import { defineComponent, ref } from 'vue';
import { ElMessage, FormInstance } from 'element-plus';
import { validateForm, validatePassword } from '../../utils/functions';
import { ConfirmRequest, ResendConfirmationEmailRequest, ResetPasswordRequest } from '@/types/types';
import { resetPassword, confirmUser, login, resendConfirmation } from '@/requests/auth';
import { getCurrentUser } from '@/requests/users';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'PasswordSetForm',
	setup() {
		const store = useStore();
		const formRef = ref<FormInstance>();
		return {
			store,
			formRef,
		};
	},
	data() {
		return {
			id: 'pwform',
			showErr: false,
			errorMsg: texts.formErrors.pwResetFailed,
			confirmationError: false,
			waiting: false,
			request: {
				password: '',
				passwordConfirm: '',
			},
			rules: {
				password: [
					{ required: true, message: texts.formErrors.pwReq, trigger: 'blur' },
					{ validator: validatePassword, trigger: 'blur' },
				],
				passwordConfirm: [
					{ required: true, message: texts.formErrors.pwReq, trigger: 'blur' },
					{ validator: this.validatePasswordConfirm, trigger: 'blur' },
				],
			},
		};
	},
	props: {
		token: {
			type: String,
			required: true,
		},
		userId: {
			type: String,
		},
		action: {
			type: String,
		},
		newEmail: {
			type: String,
		},
		email: {
			type: String,
		},
	},
	emits: ['cancel'],
	components: {
		TheForm,
		TheButton,
	},
	computed: {
		texts() {
			return texts;
		},
	},
	methods: {
		async submitForm() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;
			this.showErr = false;
			this.confirmationError = false;

			//? newEmail würde das manuelle Ändern der Email-Adresse ermöglichen, ist momentan nicht vorgesehen
			if (this.action === 'resetPassword') {
				const pwRequest = {
					email: this.email,
					resetCode: this.token,
					newPassword: this.request.password,
				} as ResetPasswordRequest;
				const result = await resetPassword(pwRequest);
				if (result.success) {
					ElMessage.success({ message: texts.formErrors.pwResetSuccess, type: 'success', duration: ElMessageDuration });
					this.$router.push({ name: 'Login' });
				} else {
					this.showErr = true;
				}
			} else if (this.action === 'confirmEmail') {
				const confirmRequest = {
					userId: this.userId,
					code: this.token,
					password: this.request.password,
				} as ConfirmRequest;

				const result = await confirmUser(confirmRequest);
				if (result.success) {
					if (this.email) {
						const loginResult = await login(
							{
								email: this.email,
								password: this.request.password,
							},
							true,
							false,
						);

						if (loginResult.success) {
							const userResult = await getCurrentUser();

							if (userResult.success) {
								this.store.dispatch('setUser', userResult.data);
								this.store.dispatch('login', true);

								this.$router.push({ name: 'Dashboard' });
							}
						} else {
							this.errorMsg = texts.formErrors.pwResetFailed;
							this.showErr = true;
						}
					} else {
						ElMessage.success({ message: texts.formErrors.pwResetSuccess, type: 'success', duration: ElMessageDuration });
						this.$router.push({ name: 'Login' });
					}
				} else {
					const msg = translateToGerman(result.error);
					if (msg) {
						this.errorMsg = msg;
					}
					this.showErr = true;
					if (result.status !== 400) {
						this.confirmationError = true;
					}
				}
			}

			this.waiting = false;
		},
		async resendConfirmationEmail() {
			this.confirmationError = false;
			this.showErr = false;
			this.waiting = true;

			if (!this.email) {
				ElMessage.error({ message: texts.formErrors.pwResendFailed, type: 'error', duration: ElMessageDuration });
				return;
			}

			const resendRequest: ResendConfirmationEmailRequest = {
				email: this.email,
			};
			const resendResult = await resendConfirmation(resendRequest);

			if (resendResult.success) {
				ElMessage.success({ message: texts.formErrors.pwResendSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage.error({ message: texts.formErrors.pwResendFailed, type: 'error', duration: ElMessageDuration });
			}
			this.waiting = false;
		},
		validatePasswordConfirm(rule, value, callback) {
			if (value === '') {
				callback(new Error(texts.formErrors.pwReq));
			} else if (value !== this.request.password) {
				callback(new Error(this.texts.formErrors.pwNoMatch));
			} else {
				callback();
			}
		},
	},
});
</script>
